import { useEffect, useSyncExternalStore } from "react";

type Theme = "light" | "dark";

const STORAGE_KEY = "theme";
const listeners = new Set<() => void>();

function getInitial(): Theme {
  if (typeof document !== "undefined") {
    const attr = document.documentElement.getAttribute("data-theme");
    if (attr === "light" || attr === "dark") return attr;
  }
  return "dark";
}

let currentTheme: Theme = getInitial();

function apply(theme: Theme) {
  currentTheme = theme;
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-theme", theme);
    document.documentElement.classList.toggle("dark", theme === "dark");
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }
  }
  listeners.forEach((l) => l());
}

export function setTheme(theme: Theme) {
  apply(theme);
}

export function toggleTheme() {
  apply(currentTheme === "dark" ? "light" : "dark");
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function getSnapshot() {
  return currentTheme;
}

export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Hydrate the in-memory theme from <html data-theme> on first mount. */
export function useThemeHydration() {
  useEffect(() => {
    const initial = getInitial();
    if (initial !== currentTheme) apply(initial);
  }, []);
}
